import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { ArrowLeft, Clock, MapPin, Loader2, AlertTriangle, Save, CalendarIcon, User, Image, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUpdateDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [update, setUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Edit state
  const [status, setStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [followUpDate, setFollowUpDate] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchUpdate();
  }, [id]);

  const fetchUpdate = async () => {
    try {
      const response = await axios.get(`${API_URL}/updates/${id}`);
      const data = response.data;
      setUpdate(data);
      setStatus(data.status);
      setAdminNotes(data.admin_notes || '');
      setFollowUpDate(data.follow_up_date ? new Date(data.follow_up_date) : null);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to load update');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        status,
        admin_notes: adminNotes,
        follow_up_date: followUpDate ? followUpDate.toISOString() : null
      };
      
      await axios.patch(`${API_URL}/admin/updates/${id}`, payload);
      toast.success('Changes saved successfully');
      setHasChanges(false);
      fetchUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (setter, value) => {
    setter(value);
    setHasChanges(true);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const nextPhoto = () => {
    setLightboxIndex((prev) => (prev + 1) % update.photos.length);
  };

  const prevPhoto = () => {
    setLightboxIndex((prev) => (prev - 1 + update.photos.length) % update.photos.length);
  };

  const getCategoryClass = (category) => {
    switch (category) {
      case 'Win Work': return 'category-win-work';
      case 'Deliver Work': return 'category-deliver-work';
      case 'Get Paid': return 'category-get-paid';
      default: return '';
    }
  };

  const getUrgencyClass = (urgency) => {
    switch (urgency) {
      case 'Low': return 'urgency-low';
      case 'Medium': return 'urgency-medium';
      case 'High': return 'urgency-high';
      default: return 'urgency-low';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-600 mb-4">{error}</p>
        <Button onClick={() => navigate('/admin')}>Go Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 page-enter">
      {/* Header */}
      <header className="bg-[#1F2A37] text-white p-4 shadow-lg sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              data-testid="back-btn"
              onClick={() => navigate('/admin')}
              className="text-white hover:bg-slate-700"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-bold tracking-tight truncate">
              Update Details
            </h1>
          </div>
          {hasChanges && (
            <Button
              data-testid="save-btn"
              onClick={handleSave}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="p-4 max-w-4xl mx-auto">
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Main Info Card */}
          <Card className="border-2 border-slate-200 shadow-lg lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-xl">{update.job_name}</CardTitle>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className={`status-badge ${getCategoryClass(update.category)}`}>
                  {update.category}
                </span>
                <span className={`status-badge ${getUrgencyClass(update.urgency)}`}>
                  {update.urgency}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Tradie Info */}
              <div className="flex items-center gap-2 text-slate-600 bg-slate-50 p-3 rounded-lg">
                <User className="w-4 h-4" />
                <span className="font-medium">{update.tradie_name}</span>
              </div>

              {/* Date/Time */}
              <div className="flex items-center gap-2 text-slate-500">
                <Clock className="w-4 h-4" />
                <span className="text-sm">
                  {format(new Date(update.created_at), 'EEEE, MMMM d, yyyy - h:mm a')}
                </span>
              </div>

              {/* Address or Contact */}
              {update.site_address && (
                <div className="flex items-start gap-2 text-slate-600">
                  <MapPin className="w-4 h-4 mt-0.5" />
                  <span>{update.site_address}</span>
                </div>
              )}

              {/* Required By */}
              {update.required_by && (
                <div className="flex items-center gap-2 text-orange-600 bg-orange-50 p-3 rounded-lg">
                  <Clock className="w-4 h-4" />
                  <span className="font-medium">
                    Required by: {format(new Date(update.required_by), 'MMM d, yyyy - h:mm a')}
                  </span>
                </div>
              )}

              {/* Photos */}
              {update.photos && update.photos.length > 0 && (
                <div className="pt-4 border-t border-slate-200">
                  <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Photos ({update.photos.length})
                  </h4>
                  <div className="grid grid-cols-4 gap-2">
                    {update.photos.map((photo, index) => (
                      <button
                        key={index}
                        onClick={() => openLightbox(index)}
                        className="aspect-square rounded-lg overflow-hidden border-2 border-slate-200 hover:border-orange-400 transition-colors"
                        data-testid={`photo-thumbnail-${index}`}
                      >
                        <img
                          src={photo}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Update Text */}
              <div className="pt-4 border-t border-slate-200">
                <h4 className="font-semibold text-slate-700 mb-2">Update Details</h4>
                <p className="text-slate-600 whitespace-pre-wrap bg-slate-50 p-4 rounded-lg">
                  {update.update_text}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Admin Actions Card */}
          <Card className="border-2 border-slate-200 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg text-orange-600">Admin Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status */}
              <div className="space-y-2">
                <Label className="font-semibold">Status</Label>
                <Select value={status} onValueChange={(v) => handleFieldChange(setStatus, v)}>
                  <SelectTrigger data-testid="status-select" className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New">
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                        New
                      </span>
                    </SelectItem>
                    <SelectItem value="In Progress">
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                        In Progress
                      </span>
                    </SelectItem>
                    <SelectItem value="Done">
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        Done
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Follow-up Date */}
              <div className="space-y-2">
                <Label className="font-semibold">Follow-up Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="followup-date-btn"
                      variant="outline"
                      className="w-full h-12 justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {followUpDate ? format(followUpDate, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={followUpDate}
                      onSelect={(date) => handleFieldChange(setFollowUpDate, date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {followUpDate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="clear-followup-btn"
                    onClick={() => handleFieldChange(setFollowUpDate, null)}
                    className="text-slate-500"
                  >
                    Clear date
                  </Button>
                )}
              </div>

              {/* Admin Notes */}
              <div className="space-y-2">
                <Label className="font-semibold">Admin Notes</Label>
                <Textarea
                  data-testid="admin-notes-input"
                  placeholder="Add notes for this update..."
                  value={adminNotes}
                  onChange={(e) => handleFieldChange(setAdminNotes, e.target.value)}
                  className="min-h-[120px]"
                />
              </div>

              {/* Save Button (mobile) */}
              <Button
                data-testid="save-btn-mobile"
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold lg:hidden"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Lightbox Modal */}
      {lightboxOpen && update.photos && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/20 rounded-full"
            onClick={closeLightbox}
            data-testid="close-lightbox-btn"
          >
            <X className="w-6 h-6" />
          </button>
          
          {update.photos.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white p-2 hover:bg-white/20 rounded-full"
                onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                data-testid="prev-photo-btn"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white p-2 hover:bg-white/20 rounded-full"
                onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                data-testid="next-photo-btn"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}
          
          <img
            src={update.photos[lightboxIndex]}
            alt={`Photo ${lightboxIndex + 1}`}
            className="max-w-[90vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
            {lightboxIndex + 1} / {update.photos.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUpdateDetail;
